'use client';

import * as Dialog from "@radix-ui/react-dialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagicWandSparkles,
  faSpinner,
  faCheck,
  faTimes
} from "@fortawesome/free-solid-svg-icons";

interface LogoConfirmationPopupProps {
  readonly isOpen: boolean;
  readonly logoPreview: string | null;
  readonly isRemovingBackground: boolean;
  readonly hasProcessedLogo: boolean;
  readonly onRemoveBackground: () => void;
  readonly onSave: () => void;
  readonly onClose: () => void;
}

export function LogoConfirmationPopup({
  isOpen,
  logoPreview,
  isRemovingBackground,
  hasProcessedLogo,
  onRemoveBackground,
  onSave,
  onClose
}: LogoConfirmationPopupProps) {
  // Prevent event propagation when clicking inside the popup
  const handleSaveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSave();
  };

  const handleRemoveBackgroundClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemoveBackground();
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay 
          className="fixed inset-0 z-[1500] bg-black/80 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[1600] w-[90vw] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-700/50 bg-slate-900 p-5 shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={handleCloseClick}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-800 hover:text-white"
            aria-label="Fechar"
          >
            <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
          </button>

          <Dialog.Title className="text-lg font-bold text-white">
            Prévia do Logo
          </Dialog.Title>
          
          <Dialog.Description className="mt-1 text-sm text-slate-400">
            Visualize seu logo e remova o fundo se necessário
          </Dialog.Description>

          {/* Logo Preview */}
          <div className="mt-4 flex items-center justify-center">
            <div className="relative overflow-hidden rounded-xl border border-slate-700">
              {/* Checkered background to show transparency */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `
                    linear-gradient(45deg, #374151 25%, transparent 25%),
                    linear-gradient(-45deg, #374151 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, #374151 75%),
                    linear-gradient(-45deg, transparent 75%, #374151 75%)
                  `,
                  backgroundSize: '16px 16px',
                  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px'
                }}
              />
              {logoPreview && (
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="relative block max-h-48 max-w-full object-contain"
                />
              )}
              
              {/* Processing overlay */}
              {isRemovingBackground && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm">
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="h-8 w-8 animate-spin text-purple-400"
                    />
                    <p className="mt-2 text-xs text-slate-300">
                      Removendo fundo...
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status indicator */}
          {hasProcessedLogo && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-emerald-400">
              <FontAwesomeIcon icon={faCheck} className="h-4 w-4" />
              <span>Fundo removido com sucesso!</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-5 flex flex-col gap-3">
            {!hasProcessedLogo && (
              <button
                type="button"
                onClick={handleRemoveBackgroundClick}
                disabled={isRemovingBackground}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-purple-500/50 bg-purple-500/10 px-4 py-3 text-sm font-semibold text-purple-400 transition hover:bg-purple-500/20 disabled:opacity-50"
              >
                {isRemovingBackground ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagicWandSparkles} className="h-4 w-4" />
                    Remover Fundo
                  </>
                )}
              </button>
            )}
            
            <button
              type="button"
              onClick={handleSaveClick}
              disabled={isRemovingBackground}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl hover:brightness-110 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faCheck} className="h-4 w-4" />
              Confirmar
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
